export type PermissionUser = {
  role: string;
  permissions?: Record<string, boolean>;
};

const STYLE_GUIDE_PERMISSION_KEY = 'adm.styleGuide.index' as const;

export function userHasPermission(user: PermissionUser | undefined | null, key: string): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const perms = user.permissions;
  if (perms && typeof perms === 'object' && Object.keys(perms).length > 0) {
    if (perms[key] === true) return true;
    if (user.role === 'admin' && key === STYLE_GUIDE_PERMISSION_KEY) return true;
    return false;
  }
  if (user.role === 'admin') return true;
  if (user.role === 'photographer') {
    return (
      key.startsWith('adm.photographer') ||
      key.includes('photographerSchedule') ||
      key.includes('photo.')
    );
  }
  if (user.role === 'partner_broker') {
    return (
      key.startsWith('adm.property') ||
      key.startsWith('adm.offer') ||
      key.startsWith('adm.document')
    );
  }
  return false;
}

export function userHasAnyPermission(user: PermissionUser | undefined | null, keys: string[]): boolean {
  return keys.some((k) => userHasPermission(user, k));
}

export const PERM = {
  scheduleAny: ['adm.schedule.panel', 'adm.schedule.show', 'adm.schedule.index'],
  propertyList: 'adm.property.index',
  propertyCreate: 'adm.property.store',
  propertyShow: 'adm.property.show',
  propertyUpdate: 'adm.property.update',
  propertyPartner: 'adm.property.indexByPartner',
  propertyDetails: 'adm.property.details',
  bulkConsultants: 'adm.properties.change.consultants',
  reportConsultant: 'adm.report.propertyByConsultant',
  reportNeighborhood: 'adm.report.propertyByNeighborhood',
  reportVisits: 'adm.report.schedulesByConsultant',
  reportVisitsByOwner: 'adm.report.schedulesByOwner',
  reportVisitsByProperty: 'adm.report.schedulesByProperty',
  reportRents: 'adm.report.rents',
  reportActives: 'adm.report.activePropertiesByDate',
  usersList: 'adm.users.index',
  usersCreate: 'adm.users.store',
  usersUpdate: 'adm.users.update',
  offerList: 'adm.offer.index',
  offerShow: 'adm.offer.show',
  offerUnread: 'adm.offer.unRead',
  faqList: 'adm.faq.index',
  faqCreate: 'adm.faq.store',
  faqUpdate: 'adm.faq.update',
  photoSchedule: 'adm.photographerSchedule.index',
  photoUpload: 'adm.photo.update',
  boardsList: 'adm.boards.index',
  boardShow: 'adm.boards.show',
  cardsStore: 'adm.cards.store',
  scheduleSearch: 'adm.schedule.search',
  contractsModels: 'adm.contractModels.get',
  contractsSend: 'adm.contracts.sendToSigners',
  contractsShow: 'adm.contracts.show',
  documents: 'adm.document.file.index',
  clientsWithoutContract: 'rentProcess.listClientsWhithoutContract',
  guaranteesList: 'adm.guarantees.index',
  guaranteesCreate: 'adm.guarantees.create',
  commercialList: 'adm.commercialRegion.getAll',
  commercialCreate: 'adm.commercialRegion.create',
  rentalBelt: 'adm.contracts.index',
  rentalUpload: 'adm.contracts.upload',

  teamsList: 'adm.teams.index',
  teamsCreate: 'adm.teams.create',
  teamsUpdate: 'adm.teams.update',
  teamsDelete: 'adm.teams.delete',

  /** Style guide UI; admins also get access when permissions map is populated (see userHasPermission). */
  styleGuide: STYLE_GUIDE_PERMISSION_KEY,

  notificationsLogs: 'adm.notifications.logs.index',
  notificationsAutomations: 'adm.notifications.automations.index',
} as const;

export function userCanCreateKanbanCard(user: PermissionUser | undefined | null): boolean {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (userHasPermission(user, PERM.cardsStore)) return true;

  const perms = user.permissions;
  const hasPopulatedMap = Boolean(perms && Object.keys(perms).length > 0);
  if (hasPopulatedMap) return false;

  return user.role === "consultant";
}

/** Permissions that enable the desktop header "Mais" dropdown (including style guide entry). */
export const PERMISSIONS_FOR_MORE_MENU: readonly string[] = [
  PERM.photoSchedule,
  PERM.clientsWithoutContract,
  PERM.faqList,
  PERM.commercialList,
  PERM.commercialCreate,
  PERM.guaranteesList,
  PERM.guaranteesCreate,
  PERM.usersList,
  PERM.usersCreate,
  PERM.bulkConsultants,
  PERM.styleGuide,
  PERM.notificationsLogs,
  PERM.notificationsAutomations,
] as const;